"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../services/api";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { showErrorToast } from "../../utils/notificationUtils";
import TransferOwnershipModal from "./modals/TransferOwnershipModal";
import ConfirmModal from "./modals/ConfirmModal";
import "./ChannelHeader.css";

const ChannelHeader = ({ channel, userRole, onOpenSidebarDrawer, onSettings, onManageMembers, onInviteMembers, onRules, onManageJoinRequests }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getUserType } = useAuth();
  const [showTransferOwnershipModal, setShowTransferOwnershipModal] =
    useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Get current user type color
  const currentUserType = getUserType();
  const userTypeColor = getUserTypeColor(currentUserType);


  const handleLeaveChannel = () => {
    if (userRole === "owner") {
      setShowTransferOwnershipModal(true);
    } else {
      setShowLeaveConfirm(true);
    }
  };

  const confirmLeaveChannel = async () => {
    try {
      await channelsAPI.leaveChannel(channel.id);
      setShowLeaveConfirm(false);
      navigate("/social/communities", {
        state: {
          channelLeftId: channel.id,
          channelLeftAt: Date.now(),
        },
      });
    } catch (error) {
      const errorMessage = handleApiError(error);
      showErrorToast(errorMessage);
    }
  };

  const handleTransferOwnership = async (newOwnerId) => {
    try {
      await channelsAPI.transferOwnership(channel.id, newOwnerId);
      navigate("/social/communities");
    } catch (error) {
      const errorMessage = handleApiError(error);
      showErrorToast(errorMessage);
    }
  };

  const isOnlyMember = (channel?.memberCount || 0) <= 1;
  const shouldShowLeaveButton = userRole && (userRole === "owner" ? !isOnlyMember : (userRole === "admin" || userRole === "member"));
  const hasManagePrivileges = userRole === "owner" || userRole === "admin";

  return (
    <div className="channel-header" style={{ '--user-type-color': userTypeColor }}>
      <div className="channel-header-body">
        {onOpenSidebarDrawer && (
          <button
            className="channel-header-sidebar-btn"
            onClick={onOpenSidebarDrawer}
            aria-label={t("channelHeader.openSidebar", "Open sidebar")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
            <span>{t("channelHeader.sidebar", "Info")}</span>
          </button>
        )}
        {shouldShowLeaveButton && (
          <button
            className="channel-header-leave-btn"
            onClick={handleLeaveChannel}
            aria-label={t("channelHeader.leaveButton", "Leave Community")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span>{t("channelHeader.leaveButton", "Leave Community")}</span>
          </button>
        )}
        <div className="channel-header-info">
          <div className="channel-profile-wrapper">
            <img
              src={channel.profileImage || "/images/social/pic-placeholder.png"}
              alt={channel.name}
              className="channel-profile-image"
              onError={(e) => {
                e.target.src = "/images/social/pic-placeholder.png";
              }}
            />
          </div>
          <div className="channel-header-details">
            <div className="channel-header-title-row">
              <h1 className="channel-header-name">{channel.name}</h1>
              {!channel.isPublic && (
                <div className="channel-header-private-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  <span>{t("channelHeader.private", "Private")}</span>
                </div>
              )}
            </div>
            <p className="channel-header-description">{channel.description}</p>
            <div className="channel-header-stats">
              <div className="channel-header-stat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span>
                  {t("channelHeader.membersCount", "{{count}} members", {
                    count: (channel?.memberCount || 0).toLocaleString(),
                  })}
                </span>

              </div>
              <div className="channel-header-stat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                <span>
                  {t("channelHeader.postsCount", "{{count}} posts", {
                    count: (channel?.postCount || 0).toLocaleString(),
                  })}
                </span>

              </div>
              <div className="channel-header-owner">
                <span>
                  {t("channelHeader.createdBy", "Created by {{name}}", {
                    name: channel.owner.name,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Quick Actions - Bottom Right */}
        {hasManagePrivileges && (
          <div className="channel-header-actions">
            {userRole === "owner" && onSettings && (
              <button
                className="channel-header-action-btn"
                onClick={onSettings}
                aria-label={t("channelDetail.sidebar.actions.settings.title", "Community settings")}
                title={t("channelDetail.sidebar.actions.settings.title", "Community settings")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <span className="channel-header-action-tooltip">{t("channelDetail.sidebar.actions.settings.title", "Community settings")}</span>
              </button>
            )}
            {onManageMembers && (
              <button
                className="channel-header-action-btn"
                onClick={onManageMembers}
                aria-label={t("channelDetail.sidebar.actions.members.title", "Manage members")}
                title={t("channelDetail.sidebar.actions.members.title", "Manage members")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span className="channel-header-action-tooltip">{t("channelDetail.sidebar.actions.members.title", "Manage members")}</span>
              </button>
            )}
            {onInviteMembers && (
              <button
                className="channel-header-action-btn"
                onClick={onInviteMembers}
                aria-label={t("channelDetail.sidebar.actions.invite.title", "Invite people")}
                title={t("channelDetail.sidebar.actions.invite.title", "Invite people")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
                <span className="channel-header-action-tooltip">{t("channelDetail.sidebar.actions.invite.title", "Invite people")}</span>
              </button>
            )}
            {onManageJoinRequests && (
              <button
                className="channel-header-action-btn"
                onClick={onManageJoinRequests}
                aria-label={t("channelHeader.manageJoinRequests", "Manage join requests")}
                title={t("channelHeader.manageJoinRequests", "Manage join requests")}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <path d="M20 8v6"></path>
                  <path d="M23 11h-6"></path>
                </svg>
                <span className="channel-header-action-tooltip">{t("channelHeader.manageJoinRequests", "Manage join requests")}</span>
              </button>
            )}
          </div>
        )}
        {/* View Members Button - For regular members only */}
        {!hasManagePrivileges && userRole === "member" && onManageMembers && (
          <div className="channel-header-actions">
            <button
              className="channel-header-action-btn"
              onClick={onManageMembers}
              aria-label={t("channelDetail.sidebar.actions.members.viewTitle", "View members")}
              title={t("channelDetail.sidebar.actions.members.viewTitle", "View members")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span className="channel-header-action-tooltip">{t("channelDetail.sidebar.actions.members.viewTitle", "View members")}</span>
            </button>
          </div>
        )}
      </div>
      {showTransferOwnershipModal && (
        <TransferOwnershipModal
          isOpen={showTransferOwnershipModal}
          onClose={() => setShowTransferOwnershipModal(false)}
          channelId={channel.id}
          channelName={channel.name}
          onTransferComplete={handleTransferOwnership}
        />
      )}

      {showLeaveConfirm && userRole !== "owner" && (
        <ConfirmModal
          isOpen={showLeaveConfirm}
          onClose={() => setShowLeaveConfirm(false)}
          onCancel={() => setShowLeaveConfirm(false)}
          onConfirm={confirmLeaveChannel}
          title={t("channelHeader.leaveConfirmTitle", "Leave community")}
          message={t(
            "channelHeader.leaveConfirm",
            "Are you sure you want to leave this community?"
          )}
          cancelLabel={t("common.cancel", "Cancel")}
          confirmLabel={t("channelHeader.leaveButton", "Leave Community")}
        />
      )}

    </div>
  );
};

export default ChannelHeader;

